import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { services } from "@/data/services";
import { AnimalTracksHog } from "@/components/AnimalTracks";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = services.find((s) => s.slug === slug);

  useEffect(() => {
    if (!service) {
      navigate("/services", { replace: true });
      return;
    }
    document.title = service.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", service.metaDescription);
    window.scrollTo(0, 0);
  }, [service, navigate]);

  if (!service) return null;

  const isHog = slug === "wild-hogs";

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid={`service-detail-${slug}`}>
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B110E] via-[#0B110E]/80 to-[#0B110E]/60" />
        </div>
        {isHog && <AnimalTracksHog className="z-[2]" />}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150 mb-8"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">{service.tagline}</p>
          <h1 className="text-[#F8FAFC] font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="service-detail-title">
            {service.title}
          </h1>
          {service.featured && (
            <div className="inline-flex items-center gap-2 bg-[#D95D39]/10 border border-[#D95D39]/30 text-[#D95D39] text-sm font-bold px-4 py-2 rounded-sm mb-6">
              Federal Swine Dealers License #FL 04513-16
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#0B110E] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[#9CA3AF] text-base sm:text-lg leading-relaxed mb-12 whitespace-pre-line">
            {service.fullDesc}
          </div>

          {/* Steps (for attic sanitation) */}
          {service.steps && (
            <div className="mb-12">
              <h2 className="text-[#F8FAFC] font-black text-2xl sm:text-3xl tracking-tighter mb-8">
                Our Process
              </h2>
              <div className="space-y-6">
                {service.steps.map((step, i) => (
                  <div key={i} className="border border-[#1F2937] rounded-sm p-6 sm:p-8 bg-[#0F1A15]">
                    <div className="flex items-start gap-4">
                      <span className="text-[#D95D39] font-black text-2xl shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <h3 className="text-[#F8FAFC] font-bold text-lg mb-2">{step.title}</h3>
                        <p className="text-[#9CA3AF] text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What we handle */}
          {service.handles.length > 0 && (
            <div className="mb-12">
              <h2 className="text-[#F8FAFC] font-black text-2xl sm:text-3xl tracking-tighter mb-6">
                We Handle
              </h2>
              <div className="space-y-3">
                {service.handles.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                    <CheckCircle className="w-5 h-5 text-[#D95D39] shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special CTA for hogs */}
          {service.cta && (
            <div className="bg-[#D95D39]/10 border border-[#D95D39]/30 rounded-sm p-6 sm:p-8 mb-12">
              <p className="text-[#F8FAFC] font-bold text-lg mb-4">{service.cta}</p>
              <a href="tel:9042376253" data-testid="service-detail-call">
                <button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-black uppercase tracking-widest text-sm px-8 py-3 rounded-sm btn-glow inline-flex items-center gap-3 transition-colors duration-150">
                  <Phone className="w-4 h-4" />
                  Call Now
                </button>
              </a>
            </div>
          )}

          {/* Bottom note for attic sanitation */}
          {service.slug === "attic-sanitation" && (
            <p className="text-[#9CA3AF] text-sm italic">
              Don't breathe in what they left behind. 50 years of experience means we've seen it all — and we know how to make it right.
            </p>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0F1A15] border-t border-[#1F2937] py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-[#F8FAFC] font-black text-2xl sm:text-3xl tracking-tighter mb-4">
            Ready to Get This Handled?
          </h2>
          <p className="text-[#9CA3AF] text-sm sm:text-base mb-8">
            Call us now or fill out the form for a free quote. We respond fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:9042376253">
              <button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-black uppercase tracking-widest text-sm px-8 py-4 rounded-sm btn-glow inline-flex items-center gap-3 transition-colors duration-150">
                <Phone className="w-5 h-5" />
                Call 904-237-6253
              </button>
            </a>
            <Link to="/contact">
              <button className="border border-[#1F2937] hover:border-[#D95D39] text-[#F8FAFC] font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-sm inline-flex items-center gap-3 transition-colors duration-150">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
